/**
 * portalRbacService.js — firma portalının yetkilerinin PERSONEL yönetimi (backlog #416 / #417, 2026-10-14; tasarım #409).
 *
 * Base: {JAVA_BACKEND_API_URL}/portal-rbac — FDS-Backend PortalRbacController. Yalnızca personel: firma girişi her uçta
 * 403 alır (D4: firma kendi kullanıcısını yönetmez). İzinler (kök hepsine sahip):
 *   portal.rol.manage          portal rolleri (tanım, modül seviyeleri, organizatör eylemleri)
 *   portal.arayuz.manage       arayüz setlerinin tavanları (yazmadan önce etki önizlemesi)
 *   portal.kullanici_rol.manage  bir firma girişine rol atama / sonlandırma, firma kısıtları (yalnızca Admin)
 *   page.firma                 firma kullanıcıları ve kısıtları salt okunur + Yetki Açıklayıcı
 * Backend her çağrıda kendisi kontrol eder; sayfalar yalnızca görünümü ayarlar. Mock yok.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_API_URL}/portal-rbac`;

/** İzin anahtarları (permission.permission_key): useYetki(...) ile sorulur, yalnızca görünüm. */
export const PORTAL_IZNI = {
  ROL: 'portal.rol.manage',
  ARAYUZ: 'portal.arayuz.manage',
  KULLANICI_ROL: 'portal.kullanici_rol.manage',
};

function parseError(error) {
  const data = error.response?.data;
  const message =
    data?.message ||
    (typeof data === 'string' && data ? data : null) ||
    error.message ||
    'Bilinmeyen hata';
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  wrapped.data = data;
  throw wrapped;
}

const get = (url, params) =>
  axios.get(`${BASE}${url}`, { headers: getAuthHeader(), params }).then((r) => r.data).catch(parseError);
const post = (url, body, params) =>
  axios.post(`${BASE}${url}`, body, { headers: getAuthHeader(), params }).then((r) => r.data).catch(parseError);
const put = (url, body, params) =>
  axios.put(`${BASE}${url}`, body, { headers: getAuthHeader(), params }).then((r) => r.data).catch(parseError);
const del = (url, params) =>
  axios.delete(`${BASE}${url}`, { headers: getAuthHeader(), params }).then((r) => r.data).catch(parseError);

// ── Portal rolleri ───────────────────────────────────────────────────────────────────────

/**
 * → { roller: [{ id, kod, ad, adTr, adEn, aciklama, aciklamaTr, aciklamaEn, sistem, aktif, sira,
 *               moduller: { MODUL: 'NONE'|'READ'|'WRITE'|'MANAGE' }, eylemler: ['STAND_EKLE'...],
 *               baglantiSayisi, varsayilanGruplar }],
 *      moduller: [...], seviyeler: [...], eylemKatalogu: [{ kod, ad, modul, gerekenSeviye }] }
 */
export const getRoller = () => get('/roller');

/** { kod, ad, adEn?, aciklama?, aciklamaEn?, sira?, moduller?, eylemler? } → oluşan rol */
export const createRol = (body) => post('/roller', body);

/** { ad?, adEn?, aciklama?, aciklamaEn?, sira?, aktif? } — sistem rolleri yeniden adlandırılabilir */
export const updateRol = (id, body) => put(`/roller/${id}`, body);

/** { moduller: { HER modül: seviye }, eylemler?: [...] } — tam seti değiştirir, yalnızca farkı uygular */
export const setRolModuller = (id, moduller, eylemler) =>
  put(`/roller/${id}/moduller`, eylemler === undefined ? { moduller } : { moduller, eylemler });

/** Kullanılmayan ÖZEL rolü siler (sistem rolü 409). */
export const deleteRol = (id) => del(`/roller/${id}`, { onay: true });

// ── Arayüz setlerinin tavanları ─────────────────────────────────────────────────────────

/** → { kaynak: 'TABLO', duzenlenebilir, arayuzler: [{ kod, ad, rol, yabanci, moduller, kodTabani, farkli }], moduller, seviyeler } */
export const getArayuzler = () => get('/arayuzler');

/**
 * degisiklikler: [{ rol, yabanci, modul, seviye }]. onay verilmezse YAZMAZ: etki önizlemesini döner
 * { yazildi: false, degisiklikler, etki: { firmaSayisi, kullaniciSayisi, firmalar: [...] } }.
 */
export const putArayuzler = (degisiklikler, { onay = false, dryRun = false, fuarId } = {}) =>
  put('/arayuzler', fuarId ? { degisiklikler, fuarId } : { degisiklikler }, { onay, dryRun });

// ── Bir firmanın girişleri, rolleri ve kısıtları ────────────────────────────────────────

/** → { firmaId, firmaAdi, kullanicilar: [{ userId, loginAdi, ad, soyad, aktif, userGroupAdi, personel, roller, sonlandirilanRoller, rolKaynagi, varsayilanRol }], roller } */
export const getFirmaKullanicilari = (firmaId) => get(`/firma/${firmaId}/kullanicilar`);

/** Girişin bu firmadaki rolleri tam olarak rolIdleri olur (diğerleri sonlandırılır, [] hepsini sonlandırır). */
export const setKullaniciRolleri = (firmaId, userId, rolIdleri) => put(`/firma/${firmaId}/kullanicilar`, { userId, rolIdleri });

/** → { firmaId, firmaAdi, kisitlar: [{ id, fuarId, modul, seviye, gerekce, aktif, olusturanAdi, olusturmaTarihi, bitirenAdi, bitisTarihi }], moduller, seviyeler } */
export const getFirmaKisitlari = (firmaId) => get(`/firma/${firmaId}/kisitlar`);

/** { modul, seviye: 'NONE'|'READ'|'WRITE', fuarId (null = her fuar), gerekce } */
export const addKisit = (firmaId, body) => post(`/firma/${firmaId}/kisitlar`, body);

/** { bitir: true } ya da { seviye?, gerekce? } (eski satır biter, yenisi başlar) */
export const updateKisit = (firmaId, kisitId, body) => put(`/firma/${firmaId}/kisitlar/${kisitId}`, body);

// ── Yetki Açıklayıcı ────────────────────────────────────────────────────────────────────

/**
 * Çözücünün tüm cevabı (salt okunur): /portal/context biçimi + birlesik + uygulananKisitlar + organizatorAyarlari.
 * userId verilmezse yalnızca firma (her set tavanında).
 */
export const getAciklama = ({ userId, firmaId, fuarId }) => get('/aciklama', { userId, firmaId, fuarId });
